import { Accordion, AccordionDetails, AccordionSummary, Divider, List, ListItem, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PageAccessMap, SubMenu } from 'src/Menus';
import { SortableSubmenu } from './SortableSubmenu';
import { NavItem } from './NavItem';
import { matchScore } from './routeMatching';
import useNavigation from '../../Providers/NavigationProvider';

interface NavGroupProps {
  label: string;
  subMenu: SubMenu;
  isSortingEnabled: boolean;
  accessKeys: string[];
}

export const NavGroup = ({ label, subMenu, isSortingEnabled, accessKeys }: NavGroupProps): React.JSX.Element | null => {
  const { isOpen, moveEntry, toggleSubMenu, activeSubMenu, isExpanded } = useNavigation();
  const { pathname } = useLocation();

  const isLegacy = subMenu === SubMenu.LEGACY;
  // The section owning the current route: always expanded and pinned. Its
  // header drops the expand chevron and shows a left accent stripe (matching
  // the active item's) instead — it can't be collapsed by clicking.
  const isActiveSection = subMenu === activeSubMenu;
  const expanded = isExpanded(subMenu);

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      moveEntry(subMenu, oldIndex, newIndex);
    },
    [moveEntry, subMenu],
  );

  // Which child item (if any) corresponds to the current route — used
  // only for the active-item highlight. Whether the section is expanded
  // is driven by the provider's route-derived active section.
  //
  // Picks the *most specific* match rather than the first: if both a
  // parent (`/picking`) and a child (`/picking/zones`) live in this
  // section, a deep pathname highlights the child instead of whichever
  // entry was listed first.
  const activeAccessKey = useMemo(() => {
    let best: string | undefined;
    let bestScore = -1;
    for (const key of accessKeys) {
      const score = matchScore(PageAccessMap[key]?.route.path, pathname);
      if (score > bestScore) {
        bestScore = score;
        best = key;
      }
    }
    return best;
  }, [accessKeys, pathname]);

  // Permission filtering upstream can leave a section with no items. Render
  // nothing rather than a phantom header (expanded) or a stray divider
  // (collapsed) for a section the user can't access.
  if (accessKeys.length === 0) {
    return null;
  }

  const items = accessKeys.map((key) => {
    const access = PageAccessMap[key];
    // accessKeys only ever holds menu-bearing entries (the provider builds
    // sections from those), so menu is present here despite being optional.
    const { icon, label } = access.menu!;

    return <NavItem key={key} icon={icon} label={label} isActive={key === activeAccessKey} to={access.route.path!} />;
  });

  // =========================================================
  // Collapsed: flat list of items, no accordion chrome
  // =========================================================
  if (!isOpen) {
    // Legacy entries are hidden from the icon-only rail entirely. They're
    // deprecated, so reaching them should be deliberate: expand the drawer
    // and open the Legacy submenu.
    if (isLegacy) {
      return null;
    }
    // When icon-only, section headers vanish — without something between
    // sections all the icons read as one long undifferentiated stack.
    // A thin trailing divider restores the grouping. `component="li"`
    // keeps the <hr> a valid child of the parent <List> (<ul>).
    return (
      <>
        {items}
        <Divider component="li" sx={{ my: 0.25 }} />
      </>
    );
  }

  // =========================================================
  // Expanded: accordion with optional sortable wrapper
  // =========================================================
  return (
    <ListItem
      disableGutters
      disablePadding
      sx={
        isLegacy
          ? {
              // Legacy stays de-emphasized via opacity, but isn't dimmed while
              // it's the section you're actually in. `mt` gives it a little
              // breathing room now that there are no section dividers.
              mt: 1,
              opacity: isActiveSection ? 1 : 0.7,
              '&:hover': { opacity: 1 },
              transition: 'opacity 150ms',
            }
          : undefined
      }
    >
      <Accordion
        square
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={() => toggleSubMenu(subMenu)}
        // Drop the default top hairline so stacked sections aren't boxed in;
        // grouping comes from the muted header labels + whitespace instead.
        sx={{ width: '100%', '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          // The active section is pinned open: drop the expand/collapse
          // chevron and flatten the cursor + hover so it doesn't read as
          // something you can click shut. It's marked current by the stronger
          // (text.primary) label and the active item pill inside — no stripe
          // on the header itself.
          expandIcon={isActiveSection ? null : <ExpandMore />}
          sx={{
            minHeight: 40,
            '& .MuiAccordionSummary-content': { my: 0.5, ...(isActiveSection && { cursor: 'default' }) },
            ...(isActiveSection && {
              cursor: 'default',
              '&:hover': { backgroundColor: 'transparent' },
            }),
          }}
        >
          {/* Unified "section label" styling for every group (matches the
              Settings nav): quiet uppercase caption that recedes behind the
              items. The active section's label is slightly stronger. */}
          <Typography
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontWeight: 700,
              fontSize: 11,
              color: isActiveSection ? 'text.primary' : 'text.secondary',
            }}
          >
            {label}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          {isSortingEnabled ? (
            <SortableSubmenu items={items} onSortEnd={onSortEnd} lockAxis={'y'} />
          ) : (
            <List disablePadding sx={{ width: '100%' }}>
              {items}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
};
